import { Button } from '@heidi/design-system/button';
import { Form } from '@heidi/design-system/form';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { CSATRating } from '@heidi/schema/ml-scribe';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { type ComponentType, useEffect, useRef } from 'react';
import { type Control, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import {
  SCRIBE_LAYOUT_CSAT_FEEDBACK_TARGET,
  SCRIBE_LAYOUT_CSAT_SURVEY_ACTION,
  type ScribeLayoutCsatAnalyticsPorts,
} from '../../contracts/scribe-layout-csat-analytics.types';

const formSchema = z.object({
  stars: z.nativeEnum(CSATRating),
  comment: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

/** The shape of the rating + comment fields the app supplies as a slot. */
export type ScribeLayoutCsatFormFieldsProps = {
  control: Control<FormValues>;
  hasStarSelected: boolean;
  onDismiss: () => void;
};

type ScribeLayoutCsatPopoverProps = {
  /**
   * The modal queue's verdict for this surface, and the whole of its visibility.
   * The queue registration stays app-side; this component never opens itself.
   */
  isOpen: boolean;
  /** The queue's close handler — dismiss, submit and Escape all route here. */
  onClose: () => void;
  formFields: ComponentType<ScribeLayoutCsatFormFieldsProps>;
  analytics: ScribeLayoutCsatAnalyticsPorts;
};

export const ScribeLayoutCsatPopover = ({
  isOpen,
  onClose,
  formFields: FormFields,
  analytics: { trackViewed, trackSubmitted },
}: ScribeLayoutCsatPopoverProps) => {
  const intl = useIntl();
  const viewedRef = useRef(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { comment: '' },
  });
  const { reset } = form;
  const starsValue = useWatch({ control: form.control, name: 'stars' });
  const hasStarSelected = starsValue !== undefined;

  useEffect(() => {
    if (!isOpen) {
      viewedRef.current = false;
      return;
    }
    if (viewedRef.current) return;

    reset({ comment: '' });
    viewedRef.current = true;
    trackViewed({
      view_action: SCRIBE_LAYOUT_CSAT_SURVEY_ACTION.shown,
    });
  }, [isOpen, reset, trackViewed]);

  const submit = form.handleSubmit((values) => {
    trackSubmitted({
      value: values.stars,
      feedback: values.comment?.trim() || undefined,
      target_type: SCRIBE_LAYOUT_CSAT_FEEDBACK_TARGET.scribeLayoutV2,
    });
    onClose();
  });

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <PopoverAnchor className="fixed bottom-4 end-4 size-0" />
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="relative w-full max-w-xs overflow-hidden"
      >
        <Form {...form}>
          <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3">
            <Button
              type="button"
              variant="ghost"
              size="xxs"
              onClick={onClose}
              className="absolute end-2 top-2"
              aria-label={intl.formatMessage({
                id: 'TDaF6JVgG6',
                defaultMessage: 'Dismiss',
              })}
            >
              <X className="size-4" />
            </Button>

            <TypographyP2Bold className="pe-6">
              <FormattedMessage
                id="udq2pjeY0e"
                defaultMessage="How are you finding the refreshed Scribe layout?"
              />
            </TypographyP2Bold>

            <FormFields
              control={form.control}
              hasStarSelected={hasStarSelected}
              onDismiss={onClose}
            />
          </form>
        </Form>
      </PopoverContent>
    </Popover>
  );
};

ScribeLayoutCsatPopover.displayName = 'ScribeLayoutCsatPopover';
